import { ApiRequestError } from '@/types/api';
import { useAuthStore } from '@/store/authStore';

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

const LOG_PREFIX = '[Gateco API]';

function getAccessToken(): string | null {
  return localStorage.getItem('access_token');
}

// --- Session refresh on 401 ---------------------------------------------
// Access tokens are short-lived (15 min). When an authenticated request
// comes back 401, try one silent refresh with the stored refresh token and
// retry the request. If that fails, clear the session and send the user to
// the login page instead of surfacing raw JWT errors in the UI.

let refreshInFlight: Promise<boolean> | null = null;

async function tryRefreshTokens(): Promise<boolean> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return false;
  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!response.ok) return false;
    const tokens = await response.json();
    if (!tokens?.access_token) return false;
    localStorage.setItem('access_token', tokens.access_token);
    if (tokens.refresh_token) {
      localStorage.setItem('refresh_token', tokens.refresh_token);
    }
    console.log(`${LOG_PREFIX} session refreshed after 401`);
    return true;
  } catch {
    return false;
  }
}

/** Single-flight: concurrent 401s share one refresh call. */
function ensureFreshTokens(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = tryRefreshTokens().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

const SESSION_EXPIRED_ERROR = {
  code: 'SESSION_EXPIRED',
  message: 'Your session has expired. Please sign in again.',
};

function redirectToLogin(): void {
  useAuthStore.getState().clearAuth();
  const target = window.location.pathname + window.location.search;
  window.location.assign(`/login?redirect=${encodeURIComponent(target)}`);
}

/**
 * Run a request; on 401 (authenticated calls only) refresh the session once
 * and retry. A second 401, or a failed refresh, ends the session.
 * `makeRequest` must build its headers on each call so the retry picks up
 * the refreshed access token.
 */
async function fetchWithAuthRetry(
  makeRequest: () => Promise<Response>,
  authenticated: boolean
): Promise<Response> {
  const response = await makeRequest();
  if (response.status !== 401 || !authenticated) {
    return response;
  }
  const refreshed = await ensureFreshTokens();
  if (refreshed) {
    const retried = await makeRequest();
    if (retried.status !== 401) {
      return retried;
    }
  }
  redirectToLogin();
  throw new ApiRequestError(SESSION_EXPIRED_ERROR, 401);
}

async function handleResponse<T>(response: Response, method: string, path: string): Promise<T> {
  console.log(`${LOG_PREFIX} ${method} ${path} → ${response.status}`);

  if (response.status === 204) {
    return undefined as T;
  }

  const data = await response.json();

  if (!response.ok) {
    console.error(`${LOG_PREFIX} ${method} ${path} error:`, data);
    if (data.error) {
      throw new ApiRequestError(data.error, response.status);
    }
    // FastAPI HTTPException sends {"detail": {"code":"...","message":"..."}} or {"detail":"string"}
    if (data.detail && typeof data.detail === 'object' && data.detail.code) {
      throw new ApiRequestError(data.detail, response.status);
    }
    // Pydantic v2 RequestValidationError sends {"detail": [{type, loc, msg, input, ctx},...]}
    // Extract all human-readable messages and join them.
    if (Array.isArray(data.detail) && data.detail.length > 0) {
      const messages = data.detail
        .map((e: Record<string, unknown>) => {
          // ctx.error has the raw ValueError message; msg has "Value error, <msg>"
          const ctx = e.ctx as Record<string, unknown> | undefined;
          return (typeof ctx?.error === 'string' ? ctx.error : null)
            ?? (typeof e.msg === 'string' ? e.msg : null)
            ?? 'Validation error';
        })
        .join(' | ');
      throw new ApiRequestError(
        { code: 'VALIDATION_ERROR', message: messages },
        response.status
      );
    }
    throw new ApiRequestError(
      {
        code: 'UNKNOWN_ERROR',
        message: typeof data.detail === 'string' ? data.detail : 'An unexpected error occurred',
      },
      response.status
    );
  }

  return data as T;
}

/**
 * GA client id from the `_ga` cookie (set on .gateco.ai by the marketing
 * site's gtag). Format: GA1.1.XXXXXXXXX.YYYYYYYYYY — the client id is the
 * last two segments. Used server-side to join product activation events
 * (e.g. retrieval_denied) to the same GA4 user as the web session.
 * Null when the user never visited the marketing site.
 */
function gaClientId(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d+\.\d+\.(\d+\.\d+)/);
  return match ? match[1] : null;
}

/**
 * GA session id from the `_ga_<STREAM>` cookie (format:
 * GS2.1.s1787311892$o1$g1...). GA4 stitches server-side Measurement
 * Protocol events to the browser session via this value — client id alone
 * joins only the user, and session-scoped attribution reads (not set).
 */
function gaSessionId(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)_ga_[A-Z0-9]+=GS\d+\.\d+\.s(\d+)/);
  return match ? match[1] : null;
}

function buildHeaders(authenticated = true): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (authenticated) {
    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const ga = gaClientId();
  if (ga) {
    headers['X-GA-Client-Id'] = ga;
  }
  const gaSession = gaSessionId();
  if (gaSession) {
    headers['X-GA-Session-Id'] = gaSession;
  }

  return headers;
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, string | number | undefined>,
  authenticated = true
): Promise<T> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    });
  }

  console.log(`${LOG_PREFIX} GET ${path}`);
  try {
    const response = await fetchWithAuthRetry(
      () => fetch(url.toString(), {
        method: 'GET',
        headers: buildHeaders(authenticated),
      }),
      authenticated
    );
    return handleResponse<T>(response, 'GET', path);
  } catch (err) {
    console.error(`${LOG_PREFIX} GET ${path} network error:`, err);
    throw err;
  }
}

export async function apiPost<T>(
  path: string,
  body?: unknown,
  authenticated = true
): Promise<T> {
  console.log(`${LOG_PREFIX} POST ${path}`, body ? JSON.stringify(body).substring(0, 200) : '');
  try {
    const response = await fetchWithAuthRetry(
      () => fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: buildHeaders(authenticated),
        body: body ? JSON.stringify(body) : undefined,
      }),
      authenticated
    );
    return handleResponse<T>(response, 'POST', path);
  } catch (err) {
    console.error(`${LOG_PREFIX} POST ${path} network error:`, err);
    throw err;
  }
}

export async function apiPatch<T>(
  path: string,
  body: unknown,
  authenticated = true
): Promise<T> {
  console.log(`${LOG_PREFIX} PATCH ${path}`);
  try {
    const response = await fetchWithAuthRetry(
      () => fetch(`${API_BASE}${path}`, {
        method: 'PATCH',
        headers: buildHeaders(authenticated),
        body: JSON.stringify(body),
      }),
      authenticated
    );
    return handleResponse<T>(response, 'PATCH', path);
  } catch (err) {
    console.error(`${LOG_PREFIX} PATCH ${path} network error:`, err);
    throw err;
  }
}

/** POST to a file-download endpoint and trigger browser download of the blob. */
export async function apiDownloadPost(
  path: string,
  params?: Record<string, string | undefined>,
  filename = 'download'
): Promise<void> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined) url.searchParams.set(k, v); });
  }
  const response = await fetchWithAuthRetry(
    () => fetch(url.toString(), {
      method: 'POST',
      headers: buildHeaders(true),
    }),
    true
  );
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiRequestError(
      data.error ?? { code: 'EXPORT_ERROR', message: data.detail ?? 'Export failed' },
      response.status
    );
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

export async function apiDelete<T = void>(
  path: string,
  authenticated = true
): Promise<T> {
  console.log(`${LOG_PREFIX} DELETE ${path}`);
  try {
    const response = await fetchWithAuthRetry(
      () => fetch(`${API_BASE}${path}`, {
        method: 'DELETE',
        headers: buildHeaders(authenticated),
      }),
      authenticated
    );
    return handleResponse<T>(response, 'DELETE', path);
  } catch (err) {
    console.error(`${LOG_PREFIX} DELETE ${path} network error:`, err);
    throw err;
  }
}
